// Edición de imagen (webhook `editor_ia` → atlas `/v1/images/edit`) y upscale
// (webhook `upscale_imagen` → atlas `/v1/images/upscale`) — ADR-601.
// Flag ADENTRO del helper:
//   ON  → atlas con JWT (`user_id` NO viaja). Respuestas idénticas:
//         edit `{response:'success', edited_image_id}` y moderación
//         `{response:'error_moderation'}` en 200 (el caller la maneja);
//         upscale `{success:true, upscaled_path}` / 402 `{success:false,
//         error:'insufficient_credits', required}`. Sin créditos en edit =
//         402 tipado → GenerateAdError billing.
//   OFF → webhooks n8n byte-idénticos (upscale conserva el proxy `/api/n8n`
//         en dev).

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import { supabase } from './supabase';

const EDITOR_IA_URL = 'https://ai-agency-dt7d.onrender.com/webhook/editor_ia';

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

/**
 * Etiqueta de analytics por MODO de edición (pedido de Matías: distinguir en
 * el panel qué tipo de edición se usó, no solo la pantalla). La usa
 * EditarImagen; el estudio y el planificador conservan su etiqueta de
 * superficie ('estudio_edicion' / 'planificador_video').
 */
export const MODO_UTILIZADO_EDICION: Record<string, string> = {
  edit_general: 'editar_imagen_general',
  edit_preciso: 'editar_imagen_precisa',
  cambio_producto: 'editar_imagen_producto',
  borrar_objeto: 'editar_imagen_borrado',
  // `cambiar_angulo: 'editar_imagen_angulo'` se quitó junto con el modo
  // (2026-08-11, no daba buenos resultados). Este mapa es sólo de ESCRITURA
  // —traduce el modo del request a la etiqueta de analytics—, así que las filas
  // históricas con 'editar_imagen_angulo' no se ven afectadas.
  // Ver ADR-2026-08-11-quitar-modo-cambiar-angulo.
};

export interface ImageEditBody {
  user_id: string;
  /** La fila a editar. Opcional SÓLO con `image_bucket`/`image_path` (modos `expandir` y
   *  `replicar`, que aceptan una foto subida). */
  imagen_id?: string | number;
  /** Foto SUBIDA (sin fila) como fuente — sólo `expandir` y `replicar`; el backend valida
   *  bucket (lista blanca por modo) y prefijo de empresa. */
  image_bucket?: string;
  image_path?: string;
  mode: string;
  instruction?: string;
  /** `expandir` y `replicar`: el formato pedido ("9:16", "4:5", …). */
  aspect_ratio?: string;
  /** Sólo `replicar`: la foto de la modelo, URL firmada de nuestro storage. */
  modelo_url?: string;
  /** Sólo `replicar`: la modelo descrita en palabras (si no hay foto). */
  descripcion_modelo?: string;
  /** Sólo `replicar`: `moda` (la prenda va puesta) o `generico` (el producto se usa/apoya). */
  rubro?: 'moda' | 'generico';
  /** Instrucciones que el usuario ya pidió sobre esta imagen en la sesión de
   *  edición, en orden. Sólo la usa `edit_general`: el backend las mete en el
   *  prompt como contexto ya aplicado. Armala con `buildEdicionesPrevias`. */
  ediciones_previas?: string[];
  mask?: string;
  /** Cómo se armó la máscara de `borrar_objeto`: detectada por SAM (`asistida`),
   *  pintada a mano (`manual`) o las dos cosas (`mixta`). El backend lo traduce al
   *  `mask_type` de Bria, que trata distinto un contorno preciso de uno a pulso. */
  mask_origen?: OrigenMascara;
  variante_id?: string | number;
  /** Sólo `replicar`: VARIOS productos (hasta 4). Con uno alcanza `variante_id`. */
  variante_ids?: string[];
  modo_utilizado?: string;
  /** "Editar igual": el usuario rechazó la sugerencia de regenerar, así que el
   *  chequeo no vuelve a correr (si no, quedaría en loop). */
  omitir_chequeo_regenerar?: boolean;
  /**
   * Sólo `edit_general`, desde la barra del visor
   * (ADR-2026-10-06-editar-con-referencias-y-escritor). Las fotos adjuntas con el contrato
   * de Redes (url firmada o `product_id`, `tipo`, `mencion`); la cadena de versiones del
   * visor, de la original a la que se ve, cada una con la instrucción que la produjo
   * (manda sobre `ediciones_previas`); y que el escritor del backend redacte el prompt,
   * elija la base y decida si viajan las vistas del producto.
   */
  reference_images?: Array<{ url?: string; tipo: string; product_id?: string; etiqueta?: string; mencion?: string }>;
  versiones?: Array<{ imagen_id: string; instruccion?: string }>;
  escribir_prompt?: boolean;
}

/** Cómo se armó la máscara del modo "Borrar objeto". */
export type OrigenMascara = 'asistida' | 'manual' | 'mixta';

/** Motivo por el que el backend sugiere regenerar en vez de editar. */
export type MotivoRegenerar = 'referencia' | 'construccion' | 'rechazo_global';

/** Cuántas instrucciones anteriores se mandan como contexto (las más recientes). */
export const MAX_EDICIONES_PREVIAS = 10;
/** Tope por instrucción. Recorta con "…" en vez de descartarla. */
export const MAX_LARGO_EDICION_PREVIA = 300;

/**
 * Recorta el historial de instrucciones a lo que viaja en el request: las últimas
 * `MAX_EDICIONES_PREVIAS`, cada una a `MAX_LARGO_EDICION_PREVIA` caracteres.
 *
 * Los topes existen por el guard de 32KB del payload de los jobs del backend, no por
 * el modelo: 10 × 300 son ~3KB, que entra cómodo. Se recorta en vez de rechazar
 * porque esto es CONTEXTO — una instrucción larga a medias sigue sirviendo, y
 * fallar la edición por el largo del historial sería peor que perder detalle.
 * Ver ADR-2026-08-12-contexto-de-ediciones-previas.
 */
export function buildEdicionesPrevias(instrucciones: string[]): string[] {
  return instrucciones
    .map((i) => i.trim())
    .filter((i) => i.length > 0)
    .slice(-MAX_EDICIONES_PREVIAS)
    .map((i) =>
      i.length > MAX_LARGO_EDICION_PREVIA
        ? `${i.slice(0, MAX_LARGO_EDICION_PREVIA)}…`
        : i,
    );
}

export interface ImageEditResponse {
  response?: string;
  edited_image_id?: string | number;
  imagen_id?: string | number;
  id?: string | number;
  error?: string;
  /** `response === 'sugerir_regenerar'`: el backend clasificó la instrucción como
   *  un pedido que la edición no puede cumplir y NO generó ni cobró. `parcial`
   *  marca que parte del pedido sí era editable. */
  motivo?: MotivoRegenerar | null;
  parcial?: boolean;
  /** Con el escritor: desde qué versión se editó (puede no ser la que se veía) y su
   *  número en la cadena (1 = original). */
  base_id?: string;
  base_version?: number;
}

// b64 (con o sin prefijo data:) → Blob para subir la máscara al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const raw = b64.includes(',') ? b64.slice(b64.indexOf(',') + 1) : b64;
  const bytes = atob(raw);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

/**
 * Edita una imagen. Devuelve el JSON parseado (incluida la moderación
 * `{response:'error_moderation'}` — la maneja cada caller). Tira
 * GenerateAdError billing en 402 y Error genérico en otros HTTP.
 *
 * `opts.companyId` es OBLIGATORIO si el body trae `mask` (edit_preciso) y el
 * flag atlas está ON: la máscara ya no viaja en el body (la cola del backend
 * rechaza binarios) — se sube al bucket `generadas` bajo el prefijo de la
 * empresa y viaja `mask_key` RELATIVA (sin ese prefijo). El fallback n8n
 * sigue mandando el b64 como siempre.
 */
export async function postImageEdit(
  body: ImageEditBody,
  opts?: { companyId?: string },
): Promise<ImageEditResponse> {
  let res: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, mask, ...atlasBody } = body;
    if (mask) {
      if (!opts?.companyId) {
        throw new Error(
          'No se pudo identificar la empresa para subir la máscara.',
        );
      }
      const maskKey = `masks/edit_${Date.now()}.png`;
      const { error: upErr } = await supabase.storage
        .from('generadas')
        .upload(`${opts.companyId}/${maskKey}`, b64ToBlob(mask), {
          contentType: 'image/png',
          upsert: false,
        });
      if (upErr) {
        console.error('[imageEdit] upload de máscara falló', upErr);
        throw new Error('No se pudo subir la máscara. Intentá de nuevo.');
      }
      (atlasBody as Record<string, unknown>).mask_key = maskKey;
    }
    res = await atlasBackendFetch('/v1/images/edit', atlasBody);
    if (res.status === 402) {
      throw new GenerateAdError('billing', BILLING_MESSAGE, 'billing');
    }
  } else {
    res = await fetch(EDITOR_IA_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
  }
  if (!res.ok) {
    throw new Error('No se pudo editar la imagen. Intentá de nuevo.');
  }
  return (await res.json()) as ImageEditResponse;
}

export interface ImageUpscaleResponse {
  success?: boolean;
  upscaled_path?: string;
  error?: string;
  /** Tipo de la taxonomía del backend (`validation`, `billing`, `technical`…). */
  error_type?: string;
  required?: number;
  plan?: string;
}

/**
 * Qué mostrarle al usuario cuando el upscale falla —
 * ADR-2026-09-22-mejorar-imagen-4x-no-entra-en-storage.
 *
 * El backend ya manda copy apto para la UI: la ruta pasa todo por `userFacingMessage`,
 * que es castellano, accionable y nunca filtra el mensaje crudo del proveedor. Las
 * superficies lo tiraban y mostraban un genérico, así que "esta imagen es demasiado
 * grande, probá con una escala menor" le llegaba al usuario como "no pudimos procesar
 * la imagen" y reintentaba lo mismo.
 *
 * `billing` queda afuera: ahí `error` es el literal `insufficient_credits`, que no es
 * un texto para mostrar — cada superficie lo maneja con su modal de límite.
 */
export function mensajeDeUpscaleFallido(data: ImageUpscaleResponse): string {
  const GENERICO = 'No pudimos procesar la imagen. Intentá nuevamente en unos minutos.';
  if (!data.error || data.error === 'insufficient_credits') return GENERICO;
  return data.error_type && data.error_type !== 'billing' ? data.error : GENERICO;
}

/**
 * Upscale de una imagen generada. Devuelve el JSON parseado en TODOS los
 * casos (éxito, `insufficient_credits`, etc.) — los callers ya branchean por
 * `data.success` / `data.error`, y el 402 del atlas trae el mismo shape de
 * error que n8n devolvía en 200.
 */
export async function postImageUpscale(body: {
  user_id: string;
  image_id: string | number;
  scale: number;
}): Promise<ImageUpscaleResponse> {
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...atlasBody } = body;
    const res = await atlasBackendFetch('/v1/images/upscale', atlasBody);
    return (await res.json()) as ImageUpscaleResponse;
  }
  // Proxy local en dev (CORS del webhook), igual que siempre.
  const n8nBase = import.meta.env.DEV
    ? '/api/n8n'
    : 'https://ai-agency-dt7d.onrender.com';
  const res = await fetch(`${n8nBase}/webhook/upscale_imagen`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
  return (await res.json()) as ImageUpscaleResponse;
}
